import { useState } from 'react';
import Layout from 'components/ui/Layout';

import {
  useSocialMediaLinks,
  useAddSocialMediaLink,
  useUpdateSocialMediaLink,
  useDeleteSocialMediaLink
} from 'features/social-media/hooks/useSocialMedia';

import { SocialMediaFormModal } from 'features/social-media/components/SocialMediaFormModal';
import { DeleteSocialMediaModal } from 'features/social-media/components/DeleteSocialMediaModal';

import {
  getPlatformLabel,
  getPlatformIcon
} from 'features/social-media/types/socialMedia.types';

import { Globe, Plus, Edit, Trash2, ExternalLink } from 'lucide-react';

const SocialMediaManagement = () => {

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);

  const { data, isPending, isError, error } = useSocialMediaLinks();

  const { mutate: addLink, isPending: isAdding } = useAddSocialMediaLink();
  const { mutate: updateLink, isPending: isUpdating } = useUpdateSocialMediaLink();
  const { mutate: deleteLink, isPending: isDeleting } = useDeleteSocialMediaLink();

  const links = data?.data || [];

  const handleOpenEdit = (link) => {
    setSelectedLink(link);
    setIsFormModalOpen(true);
  };

  const handleOpenDelete = (link) => {
    setSelectedLink(link);
    setIsDeleteModalOpen(true);
  };

  const handleFormSubmit = (formData) => {
    if (selectedLink) {
      updateLink(
        { id: selectedLink._id, data: formData },
        {
          onSuccess: () => {
            setIsFormModalOpen(false);
            setSelectedLink(null);
          }
        }
      );
    } else {
      addLink(formData, {
        onSuccess: () => {
          setIsFormModalOpen(false);
        }
      });
    }
  };

  const handleDelete = (id) => {
    deleteLink(id, {
      onSuccess: () => {
        setIsDeleteModalOpen(false);
        setSelectedLink(null);
      }
    });
  };

  return (
    <Layout>
      <div className='min-h-screen bg-background text-foreground p-6'>
        <div className='max-w-5xl mx-auto'>

          {/* Header */}

          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6'>
            <div>
              <h3 className='text-lg font-semibold text-foreground mb-1 flex items-center gap-2'>
                <Globe size={20} className='text-primary' />
                Social Media Management
              </h3>

              <p className='text-sm text-muted-foreground'>
                Manage your company's social media platform links
              </p>
            </div>

            <button
              onClick={() => {
                setSelectedLink(null);
                setIsFormModalOpen(true);
              }}
              className='flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors'
            >
              <Plus size={16} />
              Add Social Link
            </button>
          </div>

          {/* Table */}

          <div className='bg-card border border-border rounded-lg overflow-hidden'>

            {isPending ? (
              <div className='flex items-center justify-center h-64'>
                <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary' />
              </div>
            ) : isError ? (
              <div className='flex items-center justify-center h-64 text-red-400'>
                <p>Error: {error?.message || 'Failed to load links'}</p>
              </div>
            ) : links.length === 0 ? (
              <div className='flex flex-col items-center justify-center h-64 text-muted-foreground'>
                <Globe size={48} className='mb-4 opacity-50' />
                <p className='text-lg'>No social media links yet</p>
                <p className='text-sm mt-2'>Add your first link to get started</p>
              </div>
            ) : (

              <div className='overflow-x-auto'>

                <table className='w-full'>

                  <thead className='bg-input/50 border-b border-border'>
                    <tr>
                      <th className='px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                        Platform
                      </th>

                      <th className='px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                        Link
                      </th>

                      <th className='px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className='divide-y divide-border'>

                    {links.map((link) => (

                      <tr key={link._id} className='hover:bg-input/50 transition-colors'>

                        <td className='px-6 py-4 whitespace-nowrap'>

                          <div className='flex items-center gap-2'>
                            <span className='text-xl'>
                              {getPlatformIcon(link.platform)}
                            </span>

                            <span className='text-sm font-medium text-foreground'>
                              {getPlatformLabel(link.platform)}
                            </span>
                          </div>

                        </td>

                        <td className='px-6 py-4'>

                          <a
                            href={link.link}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='flex items-center gap-1.5 text-sm text-primary hover:underline max-w-xs truncate'
                          >
                            <span className='truncate'>{link.link}</span>
                            <ExternalLink size={12} className='shrink-0' />
                          </a>

                        </td>

                        <td className='px-6 py-4 whitespace-nowrap text-right'>

                          <div className='flex items-center justify-end gap-2'>

                            <button
                              onClick={() => handleOpenEdit(link)}
                              className='p-2 text-muted-foreground hover:text-primary transition-colors'
                              title='Edit'
                            >
                              <Edit size={18} />
                            </button>

                            <button
                              onClick={() => handleOpenDelete(link)}
                              className='p-2 text-muted-foreground hover:text-red-400 hover:bg-red-900/20 rounded transition-colors'
                              title='Delete'
                            >
                              <Trash2 size={18} />
                            </button>

                          </div>

                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

              </div>

            )}

          </div>

        </div>
      </div>

      {/* Form Modal */}

      <SocialMediaFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setSelectedLink(null);
        }}
        onSubmit={handleFormSubmit}
        isLoading={isAdding || isUpdating}
        initialData={selectedLink}
      />

      {/* Delete Modal */}

      <DeleteSocialMediaModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedLink(null);
        }}
        onConfirm={handleDelete}
        link={selectedLink}
        isLoading={isDeleting}
      />

    </Layout>
  );
};

export default SocialMediaManagement;